// appels/programme hooks
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isApiError } from "@core/errors";
import { toast, toastApiError } from "@core/toast";
import { cohortKeys } from "@modules/cohorts/_shared/keys";
import { appelKeys } from "../../_shared/keys";
import { classifyAppelFailure } from "../../_shared/failure";
import type { Appel } from "../../_shared/types";
import { saveProgramme } from "../services";
import type { ProgrammeValues } from "../types";

/**
 * What a failed save says, as a toast, with the reload it needs: a stale revision or an appel that
 * is no longer a draft means the page shows old data.
 */
export function useAppelFailureHandler(appelId: string) {
  const queryClient = useQueryClient();

  return (error: unknown) => {
    const failure = classifyAppelFailure(error);
    if (
      failure.kind === "revision_conflict" ||
      failure.kind === "not_editable" ||
      failure.kind === "cohort_archived" ||
      failure.kind === "not_found"
    ) {
      void queryClient.invalidateQueries({
        queryKey: appelKeys.detail(appelId),
      });
      toast.error(
        { key: `appels:errors.${failure.kind}_toast.title` },
        { key: `appels:errors.${failure.kind}_toast.description` }
      );
      return;
    }
    if (isApiError(error)) {
      toastApiError(error);
      return;
    }
    toast.error(
      { key: "appels:errors.unknown_toast.title" },
      { key: "appels:errors.unknown_toast.description" }
    );
  };
}

/**
 * Saves step 1. The answer replaces the cached appel (step 1 ticked, step 2 open) and the cohort is
 * reloaded. The toast says the appel is saved as a draft.
 */
export function useSaveProgramme(appel: Appel) {
  const queryClient = useQueryClient();
  const onFailure = useAppelFailureHandler(appel.id);

  return useMutation({
    mutationFn: (input: { values: ProgrammeValues; idempotencyKey: string }) =>
      saveProgramme(
        appel.id,
        input.values,
        appel.revision,
        input.idempotencyKey
      ),
    onSuccess: (saved) => {
      queryClient.setQueryData(appelKeys.detail(saved.id), saved);
      void queryClient.invalidateQueries({ queryKey: cohortKeys.all });
      toast.success(
        { key: "appels:programme.saved.title" },
        { key: "appels:programme.saved.description" }
      );
    },
    onError: onFailure,
  });
}
